import * as React from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Section, TitleSection, Breadcrumb, H1, Subtitle2, CreateIcon } from '@shortlyster/ui-kit';
import { SwitchLabelled, Box, Flex, Text } from '@compono/ui';
import { useParams } from 'react-router-dom';
import Link from '../../../components/Link';
import getCustomerSubtitle from '../../../lib/getCustomerSubtitle';
import OrganisationNavBar from './OrganisationNavBar';
import {
  OrganisationQuery,
  organisationQuery,
  UpdateSsoEnabledOrganisationMutation,
  updateSsoEnabledMutation,
  SsoConnections,
  ListSso,
} from '../queries';
import OrganisationsSsoList from './OrganisationSsoList';
import { useApolloClients } from 'src/lib/auth/AuthorizedApolloProvider';
import { Container, StyledButton } from 'src/components/StyledComponents';

const OrganisationSsoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { federationGatewayClient } = useApolloClients()!;

  const { data: orgQueryData, loading: orgQueryLoading } = useQuery<OrganisationQuery>(
    organisationQuery,
    {
      variables: { id },
      fetchPolicy: 'cache-and-network',
    }
  );
  const {
    data: ssoDatas,
    loading: ssoDatasloading,
    refetch: ssoDatasRefetch,
  } = useQuery<ListSso>(SsoConnections, {
    variables: { organisationId: id },
    client: federationGatewayClient,
    fetchPolicy: 'cache-and-network',
  });
  const [updateSsoEnabled, { loading: ssoUpdating }] =
    useMutation<UpdateSsoEnabledOrganisationMutation>(updateSsoEnabledMutation);

  if (orgQueryLoading || ssoDatasloading) return <p>Loading...</p>;
  if (!orgQueryData?.organisations?.length) return <p>Organisation not found...</p>;

  const organisation = orgQueryData.organisations[0];
  const isNews = organisation?.metadata?.source === 'newscorp';

  const handleSsoChange = async (checked: boolean) => {
    try {
      await updateSsoEnabled({
        variables: {
          id: organisation.id,
          ssoEnabled: checked,
        },
      });
      if (checked) {
        await ssoDatasRefetch();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderList = () => {
    if (!organisation.ssoEnabled) return null;

    return (
      <>
        <Box sx={{ width: '100%', textAlign: 'end', marginBottom: 30 }}>
          <Link to={`/organisations/${organisation.id}/sso/new`}>
            <StyledButton icon={CreateIcon}>Create SSO Connection</StyledButton>
          </Link>
        </Box>
        {ssoDatas?.ssoConnections && ssoDatas.ssoConnections.length > 0 && (
          <OrganisationsSsoList
            ssoDatas={ssoDatas.ssoConnections}
            organisationId={organisation.id}
          />
        )}
      </>
    );
  };
  return (
    <Container>
      <TitleSection>
        <Breadcrumb>
          <Link to="/organisations">Organisations</Link>
          <Link to="" aria-current={true}>
            {organisation.name}
          </Link>
        </Breadcrumb>
        <H1>{organisation.name}</H1>
        <Subtitle2>{getCustomerSubtitle(orgQueryLoading, isNews)}</Subtitle2>
      </TitleSection>
      <OrganisationNavBar organisationId={id as string} active="sso" />
      <Section>
        <Flex sx={{ alignItems: 'center', marginBottom: 3 }}>
          <Box
            sx={{
              width: ['100%'],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 10,
            }}
          >
            <Text sx={{ fontWeight: 'bold !important' }}>Enable SSO for this organisation</Text>
            <SwitchLabelled
              defaultChecked={organisation.ssoEnabled}
              size="sm"
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) =>
                handleSsoChange(event.target.checked)
              }
              disabled={ssoUpdating}
            />
          </Box>
        </Flex>
        {renderList()}
      </Section>
    </Container>
  );
};

export default OrganisationSsoPage;
